import { motion } from 'framer-motion'
import { useScrollAnimation } from '../../hooks/useScrollAnimation'
import styles from './DeepFeatures.module.css'

const features = [
  {
    title: 'Session Management',
    description: 'Unified message storage for any LLM provider. Support for multi-modal content including images, audio, and files.',
    link: 'https://github.com/luminox-mem/luminox#store',
  },
  {
    title: 'Artifact Disk',
    description: 'Save and download artifacts using file paths. Perfect for agents that generate code, documents, or media.',
    link: 'https://github.com/luminox-mem/luminox#artifacts-',
  },
  {
    title: 'Context Editing',
    description: 'Manage your context window with a single API. Built-in strategies for token reduction and compression.',
    link: 'https://github.com/luminox-mem/luminox#readme',
  },
  {
    title: 'Task Tracking',
    description: 'Automatically track agent tasks, progress, and user preferences in near real-time.',
    link: 'https://github.com/luminox-mem/luminox#observe-',
  },
  {
    title: 'Skill Learning',
    description: 'Agents learn SOPs (Standard Operating Procedures) from successful runs and apply them to future sessions.',
    link: 'https://github.com/luminox-mem/luminox#learn-',
  },
  {
    title: 'Dashboard',
    description: 'View everything in one place: sessions, tasks, success rates, and learned skills.',
    link: 'https://github.com/luminox-mem/luminox#readme',
  },
]

export default function DeepFeatures() {
  const { ref, isVisible } = useScrollAnimation<HTMLElement>()

  return (
    <section className={styles.deepFeatures} ref={ref}>
      <motion.div
        className="section-header"
        initial={{ opacity: 0, y: 20 }}
        animate={isVisible ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.5 }}
      >
        <span className="section-tag">Core Features</span>
        <h2 className="section-title">Everything You Need to Build Agents</h2>
      </motion.div>

      <div className={styles.grid}>
        {features.map((feature, index) => (
          <motion.div
            key={feature.title}
            className={styles.card}
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.4, delay: index * 0.1 }}
            whileHover={{
              y: -4,
              borderColor: 'rgba(255, 215, 0, 0.3)',
            }}
          >
            <h4>{feature.title}</h4>
            <p>{feature.description}</p>
            <motion.a
              href={feature.link}
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ color: 'var(--yellow-primary)' }}
            >
              Learn more →
            </motion.a>
          </motion.div>
        ))}
      </div>
    </section>
  )
}
